import type { FC, ReactNode } from 'react'
import type { Staff } from '@/types'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Plate } from '@/components/kit/plate'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { Button, buttonClasses } from '@/components/kit/button'
import { JobMinutes } from '@/components/jobs/job-timing'
import { useCarWash } from '@/hooks/use-carwash'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { plateLabel } from '@/lib/carwash/plate'

interface VehiclePickerSheetProps {
    staff: Staff | null
    busy: boolean
    open: boolean
    onClose: () => void
}

/* Give a staff member the next vehicle from the waiting queue. */
export const VehiclePickerSheet: FC<VehiclePickerSheetProps> = ({ staff, busy, open, onClose }): ReactNode => {
    const { state } = useCarWash()
    const actions = useCarWashActions()
    const toast = useToast()
    const [selectedId, setSelectedId] = useState<string | null>(null)

    const waiting = (state?.jobs ?? [])
        .filter(job => job.status === 'waiting')
        .sort((a, b) => a.enteredAt.localeCompare(b.enteredAt))

    useEffect(() => {
        if (open) setSelectedId(waiting[0]?.id ?? null)
        // Default to the vehicle that has waited longest, once per opening.
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!staff) return null

    const submit = (start: boolean) => {
        const job = waiting.find(item => item.id === selectedId)
        if (!job) return

        if (staff.duty === 'on_break' && !actions.setStaffDuty(staff.id, 'on_duty')) return
        if (!actions.assignStaff(job.id, staff.id, { start, allowBusyStaff: busy })) return

        const plate = plateLabel(job.plate, `#${job.number}`)
        toast({ title: start ? `${staff.name} started washing ${plate}` : `${plate} assigned to ${staff.name}` })
        onClose()
    }

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title={`Assign a vehicle to ${staff.name}`}
            description={waiting.length ? 'Vehicles in the waiting queue, longest wait first.' : undefined}
            footer={waiting.length > 0 && (
                <div className='grid grid-cols-[auto_1fr] gap-3'>
                    <Button variant='secondary' size='lg' disabled={!selectedId} onClick={() => submit(false)}>
                        Assign only
                    </Button>

                    <Button size='lg' disabled={!selectedId} onClick={() => submit(true)}>
                        Assign and start wash
                    </Button>
                </div>
            )}
        >
            {busy && (
                <p className='mb-3 rounded-lg bg-amber-50 px-3 py-2 text-[13px] font-medium text-amber-900'>
                    {`${staff.name} is already busy. They will have two vehicles at once.`}
                </p>
            )}

            {waiting.length === 0
                ? (
                    <div className='rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center'>
                        <p className='font-semibold text-foreground'>
                            No vehicles are waiting
                        </p>

                        <p className='mt-1 text-sm text-muted-foreground'>
                            Register the next vehicle and it starts with this staff member.
                        </p>

                        <Link href={`/vehicles/new?staff=${staff.id}`} onClick={onClose} className={buttonClasses({ className: 'mt-4' })}>
                            <Plus aria-hidden='true' />
                            New vehicle
                        </Link>
                    </div>
                )
                : (
                    <div role='radiogroup' aria-label='Waiting vehicles' className='space-y-2'>
                        {waiting.map(job => {
                            const selected = job.id === selectedId

                            return (
                                <button
                                    key={job.id}
                                    type='button'
                                    role='radio'
                                    aria-checked={selected}
                                    onClick={() => setSelectedId(job.id)}
                                    className={cn(
                                        'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                                        selected ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-slate-50'
                                    )}
                                >
                                    <span className='min-w-0 flex-1'>
                                        <Plate value={job.plate} size='sm' />

                                        <span className='mt-1.5 block truncate text-[13px] text-muted-foreground'>
                                            {[job.vehicleModel, job.serviceName].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>

                                    <span className='shrink-0 text-right text-[13px] font-semibold text-orange-700'>
                                        <JobMinutes job={job} />
                                        <span className='block text-[11px] font-medium text-muted-foreground'>
                                            waiting
                                        </span>
                                    </span>

                                    <span
                                        className={cn('grid size-6 shrink-0 place-items-center rounded-full border-2', selected ? 'border-primary bg-primary text-white' : 'border-slate-300')}
                                        aria-hidden='true'
                                    >
                                        {selected && <Check className='size-3.5' strokeWidth={3} />}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                )}
        </Sheet>
    )
}
